/**
 * DonationFormSection.tsx - Donation Form Configuration Section
 *
 * This component configures donation form behavior including display type,
 * modal settings, and form restrictions for the donation capture process.
 *
 * Key Features:
 * - Donation form type selection (embedded vs popup)
 * - Modal type configuration for popup forms
 * - Donation amount restrictions and limits
 * - Form behavior and validation settings
 * - Integration with donation capture components
 * - Preview of form appearance and behavior
 *
 * Form Types:
 * - Embedded: Inline form within fundraiser page
 * - Popup: Modal overlay for focused donation experience
 *
 * Modal Types (for popup forms):
 * - Simple: Basic donation form with standard fields
 * - Donation Item: Advanced form with product selection
 *
 * Restriction Settings:
 * - Minimum Donation Amount: Enforce minimum donation
 * - Maximum Donation Amount: Set upper limits if needed
 * - Donation Frequency: One-time vs recurring options
 * - Payment Methods: Card, ACH, or both
 *
 * Props: None (uses MobX store for state management)
 *
 * Dependencies: MobX store, donation form validation, amount formatting
 */

import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { fundraiserStore } from "../../../stores/FundraiserStore";
import {
  DonationAmountSelection,
  DonationFormType,
  DonationPopupModalType,
} from "../../../enums";
import { CreateFundraiserModel } from "../../../models";

const Section = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  padding: 32px;
  border: 1px solid #e2e8f0;
`;

const SectionTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SubSectionTitle = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
`;

const SubSectionSubtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin-bottom: 16px;
  line-height: 1.4;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`;

const Label = styled.label`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 18px;
  display: block;
`;

const AccountCard = styled.div<{ selected: boolean }>`
  padding: 16px 20px;
  border: 2px solid ${(props) => (props.selected ? "#3b82f6" : "#e2e8f0")};
  border-radius: 12px;
  background: ${(props) => (props.selected ? "#f0f9ff" : "#ffffff")};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 16px;
  width: 50%;

  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }
`;

const AccountHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const AccountName = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
`;

const AccountDetails = styled.div`
  color: #64748b;
  font-size: 14px;
  line-height: 1.4;
`;

const HelpText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin-top: 8px;
  line-height: 1.4;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 21px 0 0 0;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
  transition: color 0.2s ease;
  padding: 8px 0;

  &:hover {
    .radio-title {
      color: #1e293b;
    }
  }
`;

const RadioInput = styled.input`
  width: 18px;
  height: 18px;
  margin: 0;
  margin-top: 2px;
  flex-shrink: 0;
`;

const RadioContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RadioTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  line-height: 1.2;
`;

const RadioSubtitle = styled.div`
  font-size: 14px;
  color: #64748b;
  line-height: 1.4;
  cursor: pointer;
`;

const RestrictionCheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  margin-top: 30px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const RestrictionFieldsContainer = styled.div`
  display: flex;
  gap: 32px;
  align-items: center;
  flex-wrap: wrap;
  margin-left: 0px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    margin-left: 0;
  }
`;

const RestrictionFieldGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RestrictionFieldLabel = styled.label`
  font-size: 16px;
  font-weight: 400;
  color: #374151;
  min-width: 120px;
`;

const RestrictionFieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const RestrictionInput = styled.input<{ hasError?: boolean }>`
  width: 150px;
  padding: 12px 16px;
  border: 2px solid ${(props) => (props.hasError ? "#ef4444" : "#e2e8f0")};
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  background: ${(props) => (props.hasError ? "#fef2f2" : "#ffffff")};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "#ef4444" : "#3b82f6")};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const RestrictionErrorsContainer = styled.div`
  margin-top: 20px;
  margin-left: 42px; /* Move error message to the right by 42px */
`;

const RestrictionSingleError = styled.div`
  color: #ef4444;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
`;

const AmountsSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
`;

const CheckboxInput = styled.input`
  width: 18px;
  height: 18px;
  margin: 0;
`;

const CheckboxLabel = styled.label`
  font-size: 16px;
  font-weight: 400;
  color: #374151;
  cursor: pointer;
`;

const CheckboxRowIndented = styled.div`
  display: flex;
  gap: 32px;
  margin-top: 10px;
  margin-bottom: 10px;
`;

const CustomAmountsInput = styled.input<{ hasError?: boolean }>`
  width: 400px;
  padding: 12px 16px;
  border: 2px solid ${(props) => (props.hasError ? "#ef4444" : "#e2e8f0")};
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  background: ${(props) => (props.hasError ? "#fef2f2" : "#ffffff")};
  transition: all 0.2s ease;
  margin-top: 30px;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "#ef4444" : "#3b82f6")};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const InputDescription = styled.div<{ isError?: boolean }>`
  font-size: 14px;
  color: ${(props) => (props.isError ? "#ef4444" : "#64748b")};
  margin-top: 8px;
`;

/* Surcharge percentage field: number input + native spinner arrows + "%" sign
   all inside a single bordered box, matching the design. */
const SurchargeInputBox = styled.div<{ hasError?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 48px;
  padding: 0 12px;
  border: 2px solid ${(props) => (props.hasError ? "#ef4444" : "#e2e8f0")};
  border-radius: 8px;
  background: ${(props) => (props.hasError ? "#fef2f2" : "#ffffff")};
  transition: all 0.2s ease;

  &:focus-within {
    border-color: ${(props) => (props.hasError ? "#ef4444" : "#3b82f6")};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const SurchargeNumberInput = styled.input`
  width: 56px;
  border: none;
  outline: none;
  background: transparent;
  font-size: 16px;
  color: #1e293b;
  padding: 0;

  /* Always show the up/down spinner arrows (WebKit hides them until hover). */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    opacity: 1;
    height: 22px;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SurchargePercentSign = styled.span`
  font-size: 16px;
  color: #1e293b;
`;

interface DonationFormSectionProps {
  builderData: CreateFundraiserModel;
  updateBuilderField: (field: keyof CreateFundraiserModel, value: any) => void;
}

export const DonationFormSection: React.FC<DonationFormSectionProps> = observer(
  ({ builderData, updateBuilderField }) => {
    const donationFormType =
      builderData.donationFormType || DonationFormType.Embedded;
    const popupModalType =
      builderData.popupModalType || DonationPopupModalType.Simple;
    const donationRestrictionsEnabled =
      builderData.donationRestrictionsEnabled || false;
    const minDonationAmount = builderData.minDonationAmount || "";
    const maxDonationAmount = builderData.maxDonationAmount || "";
    const donationAmountsType =
      builderData.donationAmountsType || DonationAmountSelection.Default;
    const customDonationAmounts = builderData.customDonationAmounts || [
      25, 50, 100, 150, 200, 250,
    ];
    const allowCustomDonations = builderData.allowCustomDonations !== false;
    const allowMonthlyDonations = builderData.allowMonthlyDonations !== false;
    const surchargeEnabled = builderData.surchargeEnabled || false;
    const surchargePercentage = builderData.surchargePercentage || "";
    const surchargeAskCustomerFirst =
      builderData.surchargeAskCustomerFirst !== false;

    // Surcharge defaults come from the selected merchant account's card settings
    // (Settings > Payment Methods) instead of a hardcoded percentage. Read them off the
    // fetched account list - a fundraiser loaded from the API only carries the merchant
    // account's identifiers, not its payment settings.
    const selectedMerchantId = builderData.merchantAccount?.id || "";
    const selectedMerchantAccountId =
      builderData.merchantAccount?.merchantAccountId || "";
    // Match against the fetched list the way PurchaseLinkStore does: the builder's own
    // merchant account comes from deserializeAPIModel2, which carries identifiers only,
    // so its paymentSettings is an empty default and must never be used as a fallback.
    const merchantAccountWithSettings = fundraiserStore.merchantAccounts.find(
      (account) =>
        (!!selectedMerchantId && account.id === selectedMerchantId) ||
        (!!selectedMerchantAccountId &&
          account.merchantAccountId === selectedMerchantAccountId),
    );
    // surchargeDefaults() is the shared source of truth used by Checkouts and Purchase
    // Links. It already handles a card method that is switched off, or has surcharging
    // switched off, still carrying a stale rate.
    const accountDefaults = merchantAccountWithSettings?.surchargeDefaults();
    const merchantSurchargePercentage = parseFloat(
      accountDefaults?.surchargePercentage || "0",
    );
    // Only treat the account default as usable when it falls inside the range the
    // fundraiser accepts (0.1%–4%), so defaults can never block saving.
    const merchantSurchargeEnabled =
      !!accountDefaults?.surchargeEnabled &&
      !isNaN(merchantSurchargePercentage) &&
      merchantSurchargePercentage >= 0.1 &&
      merchantSurchargePercentage <= 4;
    const merchantSurchargeValue = merchantSurchargeEnabled
      ? merchantSurchargePercentage.toFixed(2)
      : "";
    const merchantSurchargeAskCustomerFirst =
      merchantSurchargeEnabled && !!accountDefaults?.surchargeAskCustomerFirst;

    const [customAmountsString, setCustomAmountsString] = useState("");
    const [customAmountsError, setCustomAmountsError] = useState("");
    const [minMaxError, setMinMaxError] = useState("");

    // Initialize custom amounts string from array only if switching to custom mode and no user input yet
    useEffect(() => {
      if (
        donationAmountsType === DonationAmountSelection.Custom &&
        customDonationAmounts.length > 0 &&
        !customAmountsString
      ) {
        const formattedAmounts = customDonationAmounts.map((amount: number) =>
          formatNumberForDisplay(amount),
        );
        setCustomAmountsString(formattedAmounts.join(", "));
      }
    }, [donationAmountsType]);

    const handlePopupModalTypeChange = (type: string) => {
      updateBuilderField("popupModalType", type);
    };

    const shouldShowDonationRestrictions = () => {
      return (
        donationFormType === DonationFormType.Embedded ||
        (donationFormType === DonationFormType.Popup &&
          popupModalType === DonationPopupModalType.Simple)
      );
    };

    const handleDonationRestrictionsToggle = (enabled: boolean) => {
      updateBuilderField("donationRestrictionsEnabled", enabled);

      if (enabled) {
        // Set default values when enabling restrictions
        updateBuilderField("minDonationAmount", "10.00");
        updateBuilderField("maxDonationAmount", "10,000.00");
      } else {
        // Clear values when disabling restrictions
        updateBuilderField("minDonationAmount", "");
        updateBuilderField("maxDonationAmount", "");
        // Clear any validation errors
        setMinMaxError("");
      }
    };

    const handleSurchargeToggle = (enabled: boolean) => {
      updateBuilderField("surchargeEnabled", enabled);
      if (enabled) {
        // Seed both the percentage and "ask donor first" from the merchant account
        // defaults, the same way a Purchase Link does when surcharge is switched on.
        if (!builderData.surchargePercentage) {
          updateBuilderField("surchargePercentage", merchantSurchargeValue);
        }
        updateBuilderField(
          "surchargeAskCustomerFirst",
          accountDefaults ? !!accountDefaults.surchargeAskCustomerFirst : true,
        );
      } else {
        updateBuilderField("surchargePercentage", "");
        updateBuilderField("surchargeAskCustomerFirst", false);
      }
    };

    // Apply the merchant account's default surcharge settings whenever the selected
    // account changes. A saved fundraiser keeps its own settings on load: the marker is
    // seeded with its merchant account id when the fundraiser data is fetched.
    useEffect(() => {
      if (!selectedMerchantId) return;
      // Wait for the account to show up in the fetched list - until then we have no
      // payment settings to read, and applying "no surcharge" would wipe the fundraiser.
      if (!accountDefaults) return;
      if (
        fundraiserStore.surchargeDefaultsAppliedForMerchantId ===
        selectedMerchantId
      ) {
        return;
      }

      fundraiserStore.surchargeDefaultsAppliedForMerchantId =
        selectedMerchantId;

      updateBuilderField("surchargeEnabled", merchantSurchargeEnabled);
      updateBuilderField("surchargePercentage", merchantSurchargeValue);
      updateBuilderField(
        "surchargeAskCustomerFirst",
        merchantSurchargeAskCustomerFirst,
      );
    }, [
      selectedMerchantId,
      merchantSurchargeEnabled,
      merchantSurchargeValue,
      merchantSurchargeAskCustomerFirst,
    ]);

    const handleMinAmountChange = (value: string) => {
      updateBuilderField("minDonationAmount", value);
    };

    const handleMaxAmountChange = (value: string) => {
      updateBuilderField("maxDonationAmount", value);
    };

    const validateMinMaxAmounts = (minValue: string, maxValue: string) => {
      if (minValue && maxValue) {
        const min = parseFloat(minValue.replace(/[^\d.]/g, ""));
        const max = parseFloat(maxValue.replace(/[^\d.]/g, ""));

        if (!isNaN(min) && !isNaN(max) && max <= min) {
          return "Maximum amount must be greater than minimum amount";
        }
      }
      return "";
    };

    const handleMinAmountBlur = () => {
      if (minDonationAmount) {
        const formatted = formatCurrencyAmount(minDonationAmount);
        updateBuilderField("minDonationAmount", formatted);

        // Validate min/max comparison
        const error = validateMinMaxAmounts(formatted, maxDonationAmount);
        setMinMaxError(error);
      }
    };

    const handleMaxAmountBlur = () => {
      if (maxDonationAmount) {
        const formatted = formatCurrencyAmount(maxDonationAmount);
        updateBuilderField("maxDonationAmount", formatted);

        // Validate min/max comparison
        const error = validateMinMaxAmounts(minDonationAmount, formatted);
        setMinMaxError(error);
      }
    };

    const shouldShowDonationAmounts = () => {
      return (
        donationFormType === DonationFormType.Embedded ||
        (donationFormType === DonationFormType.Popup &&
          popupModalType === DonationPopupModalType.Simple)
      );
    };

    const handleDonationAmountsTypeChange = (
      type: "Default" | "Custom" | "FromItem",
    ) => {
      updateBuilderField("donationAmountsType", type);

      // Handle checkbox visibility and values based on type
      if (type === "FromItem") {
        // Turn off checkboxes for "From Item"
        updateBuilderField("allowCustomDonations", false);
        updateBuilderField("allowMonthlyDonations", false);
      } else if (type === "Default" || type === "Custom") {
        // Check both checkboxes by default for "Default" and "Custom"
        updateBuilderField("allowCustomDonations", true);
        updateBuilderField("allowMonthlyDonations", true);
      }

      // Reset to default values when switching to custom mode
      if (type === "Custom") {
        const defaultAmounts = [25, 50, 100, 150, 200, 250];
        updateBuilderField("customDonationAmounts", defaultAmounts);
        const formattedAmounts = defaultAmounts.map((amount) =>
          formatNumberForDisplay(amount),
        );
        setCustomAmountsString(formattedAmounts.join(", "));
        setCustomAmountsError(""); // Clear any previous errors
      }
    };

    const getFromItemAmounts = () => {
      if (builderData.customDonationAmounts) {
        return builderData.customDonationAmounts;
      }
      return [25, 50, 100, 150, 200, 250]; // fallback
    };

    const formatNumber = (num: number): number => {
      // Round to 2 decimal places first
      const rounded = Math.round(num * 100) / 100;

      // If it's a whole number, return as integer
      if (rounded % 1 === 0) {
        return Math.round(rounded);
      }

      return rounded;
    };

    const formatNumberForDisplay = (num: number): string => {
      const rounded = Math.round(num * 100) / 100;

      // If it's a whole number, return as integer string
      if (rounded % 1 === 0) {
        return rounded.toString();
      }

      // Check decimal places
      const decimalPart = (rounded % 1).toFixed(2).substring(2);

      if (decimalPart.endsWith("0")) {
        // If ends in .X0, show one decimal place
        return rounded.toFixed(1);
      } else {
        // Show two decimal places
        return rounded.toFixed(2);
      }
    };

    const parseCustomAmountsString = (
      input: string,
    ): { amounts: number[]; isValid: boolean } => {
      const trimmed = input.trim();
      if (!trimmed) {
        return { amounts: [], isValid: false };
      }

      // Split by comma and clean up
      const parts = trimmed
        .split(",")
        .map((part) => part.trim())
        .filter((part) => part !== "");

      if (parts.length !== 6) {
        return { amounts: [], isValid: false };
      }

      const amounts: number[] = [];
      for (const part of parts) {
        const num = parseFloat(part);
        if (isNaN(num) || num <= 0) {
          return { amounts: [], isValid: false };
        }
        amounts.push(formatNumber(num));
      }

      // Check for duplicates
      const uniqueAmounts = new Set(amounts);
      if (uniqueAmounts.size !== amounts.length) {
        return { amounts: [], isValid: false };
      }

      return { amounts, isValid: true };
    };

    const handleCustomAmountsChange = (value: string) => {
      setCustomAmountsString(value);
      setCustomAmountsError("");
    };

    const handleCustomAmountsBlur = () => {
      const { amounts, isValid } =
        parseCustomAmountsString(customAmountsString);

      if (isValid) {
        // Sort amounts and update
        const sortedAmounts = amounts.sort((a, b) => a - b);
        updateBuilderField("customDonationAmounts", sortedAmounts);
        // Format each amount for display
        const formattedAmounts = sortedAmounts.map((amount) =>
          formatNumberForDisplay(amount),
        );
        setCustomAmountsString(formattedAmounts.join(", "));
        setCustomAmountsError("");
      } else {
        setCustomAmountsError(
          "Enter 6 unique numbers greater than zero and separated by commas",
        );
      }
    };

    return (
      <Section>
        <SectionTitle>Donation Form</SectionTitle>

        <FormGroup>
          <Label>Donation Form Display</Label>
          <AccountCard
            selected={donationFormType === DonationFormType.Embedded}
            onClick={() =>
              updateBuilderField("donationFormType", DonationFormType.Embedded)
            }
          >
            <AccountHeader>
              <AccountName>Embedded on Page</AccountName>
            </AccountHeader>
            <AccountDetails>
              Simple donation form embedded directly on the fundraiser page
            </AccountDetails>
          </AccountCard>
          <AccountCard
            selected={donationFormType === DonationFormType.Popup}
            onClick={() =>
              updateBuilderField("donationFormType", DonationFormType.Popup)
            }
          >
            <AccountHeader>
              <AccountName>Pop-up Modal</AccountName>
            </AccountHeader>
            <AccountDetails>
              The donation form will appear in a modal window when "Donate Now"
              is clicked
            </AccountDetails>
            {donationFormType === DonationFormType.Popup && (
              <RadioGroup>
                <RadioOption>
                  <RadioInput
                    type="radio"
                    name="popupModalType"
                    value={DonationPopupModalType.Simple}
                    checked={popupModalType === DonationPopupModalType.Simple}
                    onChange={(e) => handlePopupModalTypeChange(e.target.value)}
                  />
                  <RadioContent>
                    <RadioTitle className="radio-title">Simple Form</RadioTitle>
                    <RadioSubtitle>
                      Basic donation form with custom amount
                    </RadioSubtitle>
                  </RadioContent>
                </RadioOption>
                <RadioOption>
                  <RadioInput
                    type="radio"
                    name="popupModalType"
                    value={DonationPopupModalType.DonationItem}
                    checked={
                      popupModalType === DonationPopupModalType.DonationItem
                    }
                    onChange={(e) => handlePopupModalTypeChange(e.target.value)}
                  />
                  <RadioContent>
                    <RadioTitle className="radio-title">
                      Donation Item
                    </RadioTitle>
                    <RadioSubtitle>
                      Advanced donation form with options from Donation Item
                    </RadioSubtitle>
                  </RadioContent>
                </RadioOption>
              </RadioGroup>
            )}
          </AccountCard>
          <HelpText>
            Choose how donors will access the donation form on your fundraiser
            page.
          </HelpText>
        </FormGroup>

        {shouldShowDonationAmounts() && (
          <AmountsSection>
            <SubSectionTitle>Donation Amounts</SubSectionTitle>
            <SubSectionSubtitle>
              Select default amounts for the donation form
            </SubSectionSubtitle>

            <RadioGroup>
              <RadioOption>
                <RadioInput
                  type="radio"
                  name="donationAmountsType"
                  value="Default"
                  checked={
                    donationAmountsType === DonationAmountSelection.Default
                  }
                  onChange={(e) =>
                    handleDonationAmountsTypeChange(e.target.value as "Default")
                  }
                />
                <RadioContent>
                  <RadioTitle className="radio-title">Default</RadioTitle>
                  <RadioSubtitle>
                    Use standard amounts: $25, $50, $100, $150, $200, $250
                  </RadioSubtitle>
                  {donationAmountsType === DonationAmountSelection.Default && (
                    <CheckboxRowIndented>
                      <CheckboxContainer>
                        <CheckboxInput
                          type="checkbox"
                          id="allowCustomDonations"
                          checked={allowCustomDonations}
                          onChange={(e) =>
                            updateBuilderField(
                              "allowCustomDonations",
                              e.target.checked,
                            )
                          }
                        />
                        <CheckboxLabel htmlFor="allowCustomDonations">
                          Allow custom donations
                        </CheckboxLabel>
                      </CheckboxContainer>

                      <CheckboxContainer>
                        <CheckboxInput
                          type="checkbox"
                          id="allowMonthlyDonations"
                          checked={allowMonthlyDonations}
                          onChange={(e) =>
                            updateBuilderField(
                              "allowMonthlyDonations",
                              e.target.checked,
                            )
                          }
                        />
                        <CheckboxLabel htmlFor="allowMonthlyDonations">
                          Allow monthly donations
                        </CheckboxLabel>
                      </CheckboxContainer>
                    </CheckboxRowIndented>
                  )}
                </RadioContent>
              </RadioOption>

              <RadioOption>
                <RadioInput
                  type="radio"
                  name="donationAmountsType"
                  value="FromItem"
                  checked={
                    donationAmountsType === DonationAmountSelection.FromItem
                  }
                  onChange={(e) =>
                    handleDonationAmountsTypeChange(
                      e.target.value as "FromItem",
                    )
                  }
                />
                <RadioContent>
                  <RadioTitle className="radio-title">From Item</RadioTitle>
                  <RadioSubtitle>
                    Use amounts from selected item: $
                    {getFromItemAmounts().join(", $")}
                  </RadioSubtitle>
                </RadioContent>
              </RadioOption>

              <RadioOption>
                <RadioInput
                  type="radio"
                  name="donationAmountsType"
                  value="Custom"
                  checked={
                    donationAmountsType === DonationAmountSelection.Custom
                  }
                  onChange={(e) =>
                    handleDonationAmountsTypeChange(e.target.value as "Custom")
                  }
                />
                <RadioContent>
                  <RadioTitle className="radio-title">Custom</RadioTitle>
                  <RadioSubtitle>Set your own donation amounts</RadioSubtitle>
                  {donationAmountsType === DonationAmountSelection.Custom && (
                    <>
                      <CustomAmountsInput
                        type="text"
                        value={customAmountsString}
                        onChange={(e) =>
                          handleCustomAmountsChange(e.target.value)
                        }
                        onBlur={handleCustomAmountsBlur}
                        placeholder="Enter 6 donation amounts..."
                        hasError={!!customAmountsError}
                      />
                      <InputDescription isError={!!customAmountsError}>
                        {customAmountsError ||
                          "Enter 6 custom amounts separated by commas"}
                      </InputDescription>
                      <CheckboxRowIndented>
                        <CheckboxContainer>
                          <CheckboxInput
                            type="checkbox"
                            id="allowCustomDonationsCustom"
                            checked={allowCustomDonations}
                            onChange={(e) =>
                              updateBuilderField(
                                "allowCustomDonations",
                                e.target.checked,
                              )
                            }
                          />
                          <CheckboxLabel htmlFor="allowCustomDonationsCustom">
                            Allow custom donations
                          </CheckboxLabel>
                        </CheckboxContainer>

                        <CheckboxContainer>
                          <CheckboxInput
                            type="checkbox"
                            id="allowMonthlyDonationsCustom"
                            checked={allowMonthlyDonations}
                            onChange={(e) =>
                              updateBuilderField(
                                "allowMonthlyDonations",
                                e.target.checked,
                              )
                            }
                          />
                          <CheckboxLabel htmlFor="allowMonthlyDonationsCustom">
                            Allow monthly donations
                          </CheckboxLabel>
                        </CheckboxContainer>
                      </CheckboxRowIndented>
                    </>
                  )}
                </RadioContent>
              </RadioOption>
            </RadioGroup>
          </AmountsSection>
        )}

        {shouldShowDonationRestrictions() && (
          <div
            style={{
              marginTop: "24px",
              paddingTop: "24px",
              borderTop: "1px solid #e2e8f0",
            }}
          >
            <SubSectionTitle>Donation Restrictions</SubSectionTitle>
            <SubSectionSubtitle>
              Set limits on custom donation amounts.
              {donationRestrictionsEnabled &&
                (builderData.hasError("minDonationAmount") ||
                  builderData.hasError("maxDonationAmount")) && (
                  <span style={{ color: "#dc2626", marginLeft: "8px" }}>
                    {builderData.getErrorMessage("minDonationAmount") ||
                      builderData.getErrorMessage("maxDonationAmount")}
                  </span>
                )}
            </SubSectionSubtitle>

            <RestrictionCheckboxContainer>
              <CheckboxInput
                type="checkbox"
                id="donationRestrictions"
                checked={donationRestrictionsEnabled}
                onChange={(e) =>
                  handleDonationRestrictionsToggle(e.target.checked)
                }
              />
              <CheckboxLabel htmlFor="donationRestrictions">
                Add restrictions
              </CheckboxLabel>

              {donationRestrictionsEnabled && (
                <RestrictionFieldsContainer>
                  <RestrictionFieldGroup>
                    <RestrictionFieldRow>
                      <RestrictionFieldLabel>
                        Minimum Amount:
                      </RestrictionFieldLabel>
                      <RestrictionInput
                        type="text"
                        value={minDonationAmount}
                        onChange={(e) => handleMinAmountChange(e.target.value)}
                        onBlur={handleMinAmountBlur}
                        placeholder=""
                        hasError={
                          !!(
                            builderData.hasError("minDonationAmount") ||
                            minMaxError
                          )
                        }
                      />
                    </RestrictionFieldRow>
                  </RestrictionFieldGroup>
                  <RestrictionFieldGroup>
                    <RestrictionFieldRow>
                      <RestrictionFieldLabel>
                        Maximum Amount:
                      </RestrictionFieldLabel>
                      <RestrictionInput
                        type="text"
                        value={maxDonationAmount}
                        onChange={(e) => handleMaxAmountChange(e.target.value)}
                        onBlur={handleMaxAmountBlur}
                        placeholder=""
                        hasError={
                          !!(
                            builderData.hasError("maxDonationAmount") ||
                            minMaxError
                          )
                        }
                      />
                    </RestrictionFieldRow>
                  </RestrictionFieldGroup>
                </RestrictionFieldsContainer>
              )}
            </RestrictionCheckboxContainer>

            {/* Show min/max validation error */}
            {donationRestrictionsEnabled && minMaxError && (
              <RestrictionErrorsContainer>
                <RestrictionSingleError>{minMaxError}</RestrictionSingleError>
              </RestrictionErrorsContainer>
            )}

            {/* Show other restriction errors below the fields (not min/max comparison errors) */}
            {/* {donationRestrictionsEnabled &&
              ((errors.minDonationAmount &&
                errors.minDonationAmount !==
                  "Minimum must be less than maximum") ||
                (errors.maxDonationAmount &&
                  errors.maxDonationAmount !==
                    "Maximum must be greater than minimum")) &&
              errors.donationRestrictions && (
                <RestrictionErrorsContainer>
                  {errors.minDonationAmount &&
                    errors.minDonationAmount !==
                      "Minimum must be less than maximum" && (
                      <RestrictionSingleError>
                        {errors.minDonationAmount}.
                      </RestrictionSingleError>
                    )}
                  {errors.maxDonationAmount &&
                    errors.maxDonationAmount !==
                      "Maximum must be greater than minimum" && (
                      <RestrictionSingleError>
                        {errors.maxDonationAmount}.
                      </RestrictionSingleError>
                    )}
                  {errors.donationRestrictions && (
                    <RestrictionSingleError>
                      {errors.donationRestrictions}.
                    </RestrictionSingleError>
                  )}
                </RestrictionErrorsContainer>
              )} */}
          </div>
        )}

        {/* Not gated on the form type. Restrictions and preset amounts are genuinely
            Simple-form-only - a Donation Item form takes its amounts from the item - but the
            surcharge is charged on every donation whichever form the donor sees, because the API
            computes it from the fundraiser's own SurchargeEnabled / SurchargePercentage. Hiding it
            for "Donation Item" left the fee applying with no way to see or change it. */}
        <div
          style={{
            marginTop: "24px",
            paddingTop: "24px",
            borderTop: "1px solid #e2e8f0",
          }}
        >
          <SubSectionTitle>Surcharge</SubSectionTitle>
          <SubSectionSubtitle>
            Charge a fee to donors to cover processing costs.
          </SubSectionSubtitle>

          <RestrictionCheckboxContainer>
            <CheckboxInput
              type="checkbox"
              id="surchargeEnabled"
              checked={surchargeEnabled}
              onChange={(e) => handleSurchargeToggle(e.target.checked)}
            />
            <CheckboxLabel htmlFor="surchargeEnabled">
              Add surcharge
            </CheckboxLabel>

            {surchargeEnabled && (
              <RestrictionFieldsContainer>
                <RestrictionFieldGroup>
                  <RestrictionFieldRow>
                    <SurchargeInputBox
                      hasError={!!builderData.hasError("surchargePercentage")}
                    >
                      <SurchargeNumberInput
                        type="number"
                        step="0.1"
                        min="0.1"
                        max="4"
                        value={surchargePercentage}
                        onChange={(e) =>
                          updateBuilderField(
                            "surchargePercentage",
                            e.target.value,
                          )
                        }
                        placeholder="0.00"
                      />
                      <SurchargePercentSign>%</SurchargePercentSign>
                    </SurchargeInputBox>
                    <RestrictionFieldLabel>
                      to every donation
                    </RestrictionFieldLabel>
                  </RestrictionFieldRow>
                </RestrictionFieldGroup>
                <RestrictionFieldGroup>
                  <RestrictionFieldRow>
                    <CheckboxInput
                      type="checkbox"
                      id="surchargeAskCustomerFirst"
                      checked={surchargeAskCustomerFirst}
                      onChange={(e) =>
                        updateBuilderField(
                          "surchargeAskCustomerFirst",
                          e.target.checked,
                        )
                      }
                    />
                    <CheckboxLabel htmlFor="surchargeAskCustomerFirst">
                      Ask donor first
                    </CheckboxLabel>
                  </RestrictionFieldRow>
                </RestrictionFieldGroup>
              </RestrictionFieldsContainer>
            )}
          </RestrictionCheckboxContainer>

          {surchargeEnabled && builderData.hasError("surchargePercentage") && (
            <RestrictionErrorsContainer>
              <RestrictionSingleError>
                {builderData.getErrorMessage("surchargePercentage")}
              </RestrictionSingleError>
            </RestrictionErrorsContainer>
          )}
        </div>
      </Section>
    );
  },
);

export const formatCurrencyAmount = (value: string): string => {
  if (!value) return "";
  // Remove all non-numeric characters except decimal point
  const numericValue = value.replace(/[^\d.]/g, "");
  const num = parseFloat(numericValue);

  if (isNaN(num)) return "";

  // Format with 2 decimal places and commas
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};
